/** Path under REACT_APP_CLIENT_ASSET_URL for Ellensburg WA withholding PDF. */
export const ELLENSBURG_WA_READ_DOCUMENT_PATH =
  "doc/4.d%20WA%20F700-046-000%202025.pdf";

/** Full URL: REACT_APP_CLIENT_ASSET_URL + path; falls back to legacy CDN if env is unset. */
export const ELLENSBURG_WA_READ_DOCUMENT_HREF = (() => {
  return `https://d2y6ehs3vqj7st.cloudfront.net/${ELLENSBURG_WA_READ_DOCUMENT_PATH}`;
})();

export const RESOURCE_CREW_LOCATIONS = {
  REDMOND_OR: "Redmond, OR",
  SPRINGFIELD_OR: "Springfield, OR",
  ELLENSBURG_WA: "Ellensburg, WA",
  BOISE_ID: "Boise, ID",
  ASHEVILLE_NC: "Asheville, NC",
  /** Alias for common misspelling */
  ASHVILLE_NC: "Ashville, NC",
  /** State-only label when crew is filed under NC */
  NC: "NC",
};

export const STATE_TAX_UI_TYPES = {
  REDMOND_OR: "redmond_or",
  SPRINGFIELD_OR: "springfield_or",
  ELLENSBURG_WA: "ellensburg_wa",
  BOISE_ID: "boise_id",
  ASHEVILLE_NC: "asheville_nc",
};

const RESOURCE_CREW_TYPE_TO_UI_TYPE = {
  [RESOURCE_CREW_LOCATIONS.REDMOND_OR]: STATE_TAX_UI_TYPES.REDMOND_OR,
  [RESOURCE_CREW_LOCATIONS.SPRINGFIELD_OR]: STATE_TAX_UI_TYPES.SPRINGFIELD_OR,
  [RESOURCE_CREW_LOCATIONS.ELLENSBURG_WA]: STATE_TAX_UI_TYPES.ELLENSBURG_WA,
  [RESOURCE_CREW_LOCATIONS.BOISE_ID]: STATE_TAX_UI_TYPES.BOISE_ID,
  /** DB `list_resource_type.key` when API sends key instead of label */
  boise_id: STATE_TAX_UI_TYPES.BOISE_ID,
  ellensburg_wa: STATE_TAX_UI_TYPES.ELLENSBURG_WA,
  [RESOURCE_CREW_LOCATIONS.ASHEVILLE_NC]: STATE_TAX_UI_TYPES.ASHEVILLE_NC,
  [RESOURCE_CREW_LOCATIONS.ASHVILLE_NC]: STATE_TAX_UI_TYPES.ASHEVILLE_NC,
  [RESOURCE_CREW_LOCATIONS.NC]: STATE_TAX_UI_TYPES.ASHEVILLE_NC,
};

const BOISE_STATE_TAX_FORM_SIGNAL_FIELDS = [
  "boise_worksheet_other_jobs",
  "boise_worksheet_other_income",
  "boise_worksheet_itemized_line4",
  "boise_worksheet_pay_periods",
  "boise_worksheet_itemized_line_1",
  "boise_worksheet_itemized_line_2",
  "boise_worksheet_itemized_line_3",
  "boise_worksheet_itemized_line_4",
  "boise_worksheet_itemized_line_5",
  "boise_worksheet_itemized_line_6",
  "boise_worksheet_itemized_line_7",
  "boise_worksheet_itemized_line_8",
  "boise_worksheet_itemized_line_9",
];

function formSuggestsBoiseStateTax(form) {
  if (!form || typeof form !== "object") {
    return false;
  }
  return BOISE_STATE_TAX_FORM_SIGNAL_FIELDS.some((k) => {
    const v = form[k];
    return v !== undefined && v !== null && String(v).trim() !== "";
  });
}

/** NC-4 multi-page form (Asheville) — any persisted `nc4_*` field with a real value */
function formSuggestsAshevilleNcStateTax(form) {
  if (!form || typeof form !== "object") {
    return false;
  }
  return Object.keys(form).some((key) => {
    if (!key.startsWith("nc4_")) return false;
    const v = form[key];
    if (v === undefined || v === null || v === false) return false;
    if (v === "") return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  });
}

export const STATE_TAX_UI_CONFIG = {
  [STATE_TAX_UI_TYPES.REDMOND_OR]: {
    title: "State Tax – Redmond, OR",
    description:
      "Please complete the state tax withholding form below for Redmond, OR.",
    variantClass: "state-tax-redmond-or",
  },
  [STATE_TAX_UI_TYPES.SPRINGFIELD_OR]: {
    title: "State Tax – Springfield, OR",
    description:
      "Please complete the state tax withholding form below for Springfield, OR.",
    variantClass: "state-tax-springfield-or",
  },
  [STATE_TAX_UI_TYPES.ELLENSBURG_WA]: {
    title: "State Tax – Ellensburg, WA",
    description:
      "Please complete the state tax withholding form below for Ellensburg, WA.",
    variantClass: "state-tax-ellensburg-wa",
    /** Linked from the onboarding “read document” box (WA F700-046-000 2025 PDF). */
    readDocumentHref: ELLENSBURG_WA_READ_DOCUMENT_HREF,
    readDocumentLinkText: "Read document",
  },
  [STATE_TAX_UI_TYPES.BOISE_ID]: {
    title: "State Tax – Boise, ID",
    description:
      "Please complete the state tax withholding form below for Boise, ID.",
    variantClass: "state-tax-boise-id",
  },
  [STATE_TAX_UI_TYPES.ASHEVILLE_NC]: {
    title: "State Tax – Asheville, NC",
    description:
      "Please complete the state tax withholding form below for Asheville, NC.",
    variantClass: "state-tax-asheville-nc",
  },
};

export const OREGON_UI_TYPES = [
  STATE_TAX_UI_TYPES.REDMOND_OR,
  STATE_TAX_UI_TYPES.SPRINGFIELD_OR,
];

export const DEFAULT_STATE_TAX_UI_TYPE = STATE_TAX_UI_TYPES.REDMOND_OR;

export function getStateTaxTypeFromResourceCrewType(resourceCrewType) {
  if (!resourceCrewType || typeof resourceCrewType !== "string") {
    return DEFAULT_STATE_TAX_UI_TYPE;
  }
  const trimmed = resourceCrewType.trim();
  const mapped = RESOURCE_CREW_TYPE_TO_UI_TYPE[trimmed];
  if (mapped) {
    return mapped;
  }
  const lower = trimmed.toLowerCase();
  if (trimmed.toUpperCase() === "NC" || lower === "north carolina") {
    return STATE_TAX_UI_TYPES.ASHEVILLE_NC;
  }
  if (lower.includes("asheville") || lower.includes("ashville")) {
    return STATE_TAX_UI_TYPES.ASHEVILLE_NC;
  }
  if (lower.includes("boise") || lower.includes("idaho")) {
    return STATE_TAX_UI_TYPES.BOISE_ID;
  }
  if (lower.includes("ellensburg")) {
    return STATE_TAX_UI_TYPES.ELLENSBURG_WA;
  }
  return DEFAULT_STATE_TAX_UI_TYPE;
}

/**
 * Prefer crew location; if unknown (defaults to Oregon), infer Boise from saved Idaho W-4 worksheet fields.
 */
export function getStateTaxTypeFromResourceCrewTypeAndForm(resourceCrewType, form) {
  const fromCrew = getStateTaxTypeFromResourceCrewType(resourceCrewType);
  if (formSuggestsBoiseStateTax(form)) {
    return STATE_TAX_UI_TYPES.BOISE_ID;
  }
  if (formSuggestsAshevilleNcStateTax(form)) {
    return STATE_TAX_UI_TYPES.ASHEVILLE_NC;
  }
  if (fromCrew !== DEFAULT_STATE_TAX_UI_TYPE) {
    return fromCrew;
  }
  return fromCrew;
}
